import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import proxy from '../api/axios';

const ACCENT = '#137274';

const STATUS_META = {
  'not-started': { label: 'Not started', color: '#2563eb' },
  'in-progress': { label: 'In progress', color: '#d97706' },
  submitted: { label: 'Submitted', color: '#1a7f37' },
};

// A test not yet attempted is gated by its scheduling window; once started
// (in-progress/submitted) the window no longer matters for display — the
// backend clamps an in-progress attempt's own clock to the window instead.
const getScheduleState = (test) => {
  if (test.status !== 'not-started' || !test.startTime || !test.endTime) return null;
  const now = Date.now();
  if (now < new Date(test.startTime).getTime()) return 'upcoming';
  if (now > new Date(test.endTime).getTime()) return 'closed';
  return 'live';
};

const NeetTestsPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark ? '#0f1419' : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restricted, setRestricted] = useState(false);
  const [starting, setStarting] = useState(null);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setRestricted(false);
    try {
      const { data } = await proxy.get('/neet-tests');
      setTests(Array.isArray(data) ? data : []);
    } catch (error) {
      if (error.response?.status === 403) {
        setRestricted(true);
      } else {
        toast.error('Failed to load NEET tests');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  const handleAction = async (test) => {
    if (test.status === 'submitted') {
      navigate(`/neet-tests/${test._id}/result`);
      return;
    }
    // Fullscreen needs a user gesture — request it synchronously here, right
    // on the click, rather than later in NeetTestLive (an async/mount-time
    // call would be silently blocked by the browser).
    document.documentElement.requestFullscreen?.().catch(() => {});
    if (test.status === 'in-progress') {
      navigate(`/neet-tests/${test._id}/live`);
      return;
    }
    setStarting(test._id);
    try {
      await proxy.post(`/neet-tests/${test._id}/start`);
      navigate(`/neet-tests/${test._id}/live`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not start test');
    } finally {
      setStarting(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, bgcolor: pageBg }}>
        <CircularProgress size={28} sx={{ color: ACCENT }} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading NEET tests…</Typography>
      </Box>
    );
  }

  if (restricted) {
    return (
      <Box sx={{ minHeight: '60vh', bgcolor: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 420 }}>
          <LockOutlinedIcon sx={{ fontSize: 48, color: muted, mb: 1.5 }} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 0.5 }}>NEET tests aren't enabled for your account</Typography>
          <Typography variant="body2" sx={{ color: muted }}>
            Ask an admin to grant you NEET test access to see mock tests here.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: pageBg, px: { xs: 1.5, sm: 3 }, py: 3 }}>
      <Helmet>
        <title>NEET Mock Tests | CrafterHack</title>
      </Helmet>
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Typography sx={{ fontWeight: 900, fontSize: '1.5rem', letterSpacing: '-0.03em', mb: 0.5 }}>
          NEET mock tests
        </Typography>
        <Typography variant="body2" sx={{ color: muted, mb: 3 }}>
          Timed, NEET-pattern practice tests with negative marking. Each test can only be attempted once.
        </Typography>

        {tests.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, borderRadius: 2.5, border: `1px dashed ${border}`, bgcolor: surface }}>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No tests available yet</Typography>
            <Typography variant="body2" sx={{ color: muted }}>Check back later.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {tests.map((test) => {
              const meta = STATUS_META[test.status] || STATUS_META['not-started'];
              const scheduleState = getScheduleState(test);
              return (
                <Box
                  key={test._id}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
                      <Chip
                        size="small"
                        label={meta.label}
                        sx={{ height: 22, fontWeight: 800, fontSize: '0.68rem', bgcolor: alpha(meta.color, isDark ? 0.22 : 0.1), color: meta.color }}
                      />
                      <Chip
                        size="small"
                        icon={<TimerOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${test.durationMinutes} min`}
                        sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem' }}
                      />
                      <Chip size="small" label={`${test.questionCount} questions`} sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem' }} />
                      <Chip size="small" label={`${test.totalMarks} marks`} sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <FactCheckOutlinedIcon sx={{ fontSize: 18, color: ACCENT, flexShrink: 0 }} />
                      <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', sm: '1rem' } }}>{test.title}</Typography>
                    </Box>
                    {test.description && (
                      <Typography variant="body2" sx={{ color: muted, fontSize: '0.85rem', mt: 0.35 }}>
                        {test.description}
                      </Typography>
                    )}
                    {test.status === 'submitted' && test.score != null && (
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mt: 0.5, color: ACCENT }}>
                        Your score: {test.score} / {test.maxScore}
                      </Typography>
                    )}
                    {scheduleState === 'upcoming' && (
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mt: 0.5, color: '#d97706' }}>
                        Opens {new Date(test.startTime).toLocaleString()}
                      </Typography>
                    )}
                    {scheduleState === 'closed' && (
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', mt: 0.5, color: '#cf222e' }}>
                        Closed since {new Date(test.endTime).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: { xs: '100%', sm: 'auto' } }}>
                    <Tooltip title="Leaderboard">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/neet-tests/${test._id}/leaderboard`)}
                        sx={{ border: `1px solid ${border}`, flexShrink: 0 }}
                      >
                        <LeaderboardOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="contained"
                      disabled={starting === test._id || scheduleState === 'upcoming' || scheduleState === 'closed'}
                      onClick={() => handleAction(test)}
                      sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, boxShadow: 'none', minHeight: 40, flex: { xs: 1, sm: 'initial' }, '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' } }}
                    >
                      {starting === test._id
                        ? 'Starting…'
                        : test.status === 'submitted'
                          ? 'View result'
                          : test.status === 'in-progress'
                            ? 'Resume'
                            : scheduleState === 'upcoming'
                              ? 'Not open yet'
                              : scheduleState === 'closed'
                                ? 'Closed'
                                : 'Start test'}
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NeetTestsPage;
